import * as React from 'react';
import styled from '@emotion/styled';
import { useEditorHandlers } from './@editor';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppContext } from '../../contexts/AppContext';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FlagIcon from '@mui/icons-material/Flag';
import { Link } from '../Link';

interface LanguageSelectMenuProps {
  size?: 'small' | 'medium' | 'large' | undefined;
  edge?: false | 'end' | 'start' | undefined;
}

interface LanguageToSomethingProps {
  [key: string]: string
}

const ButtonStyled = styled(Button)(({ theme }) => ({
  backgroundColor: theme.theme_component_language_menu_button_background_color || theme.palette.primary.light,
  borderRadius: theme.shape.borderRadius,
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  width: 100
})) as typeof Button;

const ButtonLabel = styled(Typography)(({ theme }) => ({
  whiteSpace: 'nowrap',
  textTransform: 'capitalize',
  color: theme.theme_component_language_menu_button_button_label_color || theme.palette.primary.contrastText,
})) as typeof Typography;

const ButtonDownIcon = styled(ArrowDropDownIcon)(({ theme }) => ({
  marginLeft: 'auto',
  color: theme.theme_component_language_menu_button_down_icon_color || theme.palette.primary.contrastText,
}));

const ButtonFlagIcon = styled(FlagIcon)(({ theme }) => ({
  color: theme.theme_component_language_menu_button_down_icon_color || theme.palette.primary.contrastText,
}));

const Flag = styled('span')(({ theme }) => ({
  marginRight: theme.spacing(2),
  marginBottom: '-3px',
  [theme.breakpoints.down('sm')]: {
    marginRight: theme.spacing(1),
  },
}));

const ItemLabel = styled(Typography)(({ theme }) => ({
  color: theme.theme_component_language_menu_item_color || theme.palette.text.primary,
})) as typeof Typography;

const PaperStyled = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.theme_component_language_menu_paper_background_color || theme.palette.background.paper,
})) as typeof Paper;

const languageToCountryCodeMapping: LanguageToSomethingProps = Object.freeze({
  nl: 'NL',
  it: 'IT',
  tr: 'TR',
  en: 'GB',
  sa: 'SA',
  pt: 'PT',
  bg: 'BG',
  hr: 'HR',
  cz: 'CZ',
  dk: 'Dk',
  fi: 'FI',
  fr: 'FR',
  de: 'DE',
  gr: 'GR',
  in: 'IN',
  hu: 'HU',
  jp: 'JP',
  kr: 'KR',
  lv: 'LV',
  lt: 'LT',
  ro: 'RO',
  ru: 'RU',
  rs: 'RS',
  cn: 'CN',
  sk: 'SK',
  es: 'ES',
  se: 'SE',
  ua: 'UA',
  no: 'NO',
});

export const LanguageSelectMenu = (props: LanguageSelectMenuProps) => {
  const { size, edge } = props;
  const theme = useTheme();
  const { locale = 'en', locales = ['en']} = useRouter();
  const { t } = useTranslation('language_select_menu');
  const { pathname, query } = useRouter();
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const appContext = useAppContext();
  const { componentOptions } = appContext.useConfig();

  const flagDisabled = componentOptions.component_language_menu_flags_disabled;
  const languageNameDisabled = componentOptions.component_language_menu_the_language_names_disabled;

  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const prevOpen = React.useRef<boolean>(open);

  const languageToNameList: LanguageToSomethingProps = {
    en: t('en', 'English'),
    it: t('it', 'Italian'),
    nl: t('nl', 'Dutch'),
    pt: t('pt', 'Portuguese'),
    tr: t('tr', 'Türkçe'),
    no: t('no', 'Norsk'),
  };

  const countryToFlag = (isoCode: string) => typeof String.fromCodePoint !== 'undefined'
    // @ts-ignore
    ? isoCode.toUpperCase().replace(/./g, (char) => String.fromCodePoint(char.codePointAt(0) + 127_397))
    : isoCode;

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    // @ts-ignore
    if (event && anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  React.useEffect(() => {
    // Return focus to the button when we transitioned from !open -> open.
    if (anchorRef.current && prevOpen.current && !open) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <Box sx={{ display: 'inline-flex' }} {...useEditorHandlers('Language Select Menu')}>
      {lg ? (
        <ButtonStyled
          ref={anchorRef}
          id="language-select-button"
          aria-controls={open ? 'language-select-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          size={size}
          onClick={handleToggle}
          variant="contained"
        >
          {!flagDisabled && (
            <Flag>
              {countryToFlag(languageToCountryCodeMapping[locale] || 'EU')}
            </Flag>
          )}
          {!languageNameDisabled && (
            <ButtonLabel variant="inherit" textAlign="left">
              {languageToNameList[locale]}
            </ButtonLabel>
          )}
          <ButtonDownIcon/>
        </ButtonStyled>
      ) : (
        <IconButton
          ref={anchorRef}
          id="language-select-button"
          aria-controls={open ? 'language-select-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          size={size}
          edge={edge}
          onClick={handleToggle}
        >
          <ButtonFlagIcon/>
        </IconButton>
      )}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        disablePortal
        placeholder=""
        style={{
          zIndex: 1000,
          // minWidth: '100%',
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'right top' : 'right bottom',
            }}
          >
            <PaperStyled>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="language-select-menu"
                  aria-labelledby="language-select-button"
                  onKeyDown={handleListKeyDown}
                >
                  {locales.map((locale: string, index: number) => (
                    <MenuItem
                      key={index}
                      component={Link}
                      href={{ pathname, query }}
                      locale={locale}
                    >
                      <Flag>
                        {countryToFlag(languageToCountryCodeMapping[locale] || 'EU')}
                      </Flag>
                      <ItemLabel variant="inherit" textAlign="left">
                        {languageToNameList[locale]}
                      </ItemLabel>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </PaperStyled>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}